import { Static, Type } from '@sinclair/typebox';

/**
 * Détail de paiement/remboursement d'une réservation, source unique de la **timeline** de paiement
 * (composant front `PaymentTimeline`). Partagé par le tunnel booking classique (via
 * `PreReservationResponseSchema` / `BookingConfirmationResponseSchema`) ET le tunnel VTC (via
 * `RidePriceCalculatorResponseSchema`). Défini dans un module neutre pour éviter le cycle
 * d'import `professional.ts` ↔ `pre_reservation.ts`.
 *
 * Champs `is_price` = chaînes formatées (devise/locale) ; les `_raw` sont les entiers (unités
 * mineures) utilisés pour les tests de branche côté front.
 */
export const PaymentInfosSchema = Type.Object({
  total_amount: Type.String({ meta: { is_price: true } }),
  total_amount_raw: Type.Number(),
  // Centimes. Total TTC de TOUTES les prestations réservées (celles payées sur place comprises)
  // moins le montant couvert par un bon ou une carte cadeau, plancher 0 : sert de `value` à
  // l'événement de suivi `booking_confirmed`. `total_amount_raw` ne convient pas (hors
  // prestations payées sur place, avant le bon). Optionnel le temps d'un déploiement progressif.
  net_total_raw: Type.Optional(Type.Number()),
  amount_to_pay_today: Type.String({ meta: { is_price: true } }),
  amount_to_pay_today_raw: Type.Number(),
  deposit_amount: Type.Optional(Type.String({ meta: { is_price: true } })),
  // Total de la réservation APRÈS remise (carte cadeau ou code promo) = payé aujourd'hui + reste à
  // régler sur place. Le tunnel l'affiche comme total du récapitulatif quand une remise s'applique :
  // le total du panier (`cart-summary`) est calculé sans code.
  total_after_discount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'total_after_discount_raw' } }),
  ),
  total_after_discount_raw: Type.Optional(Type.Number()),
  remaining_amount_on_site: Type.String({ meta: { is_price: true } }),
  possible_refund_in_time: Type.String({ meta: { is_price: true, source_key: 'possible_refund_in_time_raw' } }),
  possible_refund_in_time_raw: Type.Optional(Type.Number()),
  possible_refund_out_time: Type.String({ meta: { is_price: true, source_key: 'possible_refund_out_time_raw' } }),
  possible_refund_out_time_raw: Type.Optional(Type.Number()),
  cancellation_fee_in_time: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'cancellation_fee_in_time_raw' } }),
  ),
  cancellation_fee_in_time_raw: Type.Optional(Type.Number()),
  cancellation_fee_out_time: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'cancellation_fee_out_time_raw' } }),
  ),
  cancellation_fee_out_time_raw: Type.Optional(Type.Number()),
});
export type PaymentInfosObject = Static<typeof PaymentInfosSchema>;

/**
 * Ligne d'un achat pour les événements de suivi des sites clients (format GA4 `items`), commune à
 * la pré-réservation (`booking_confirmed`) et à l'achat d'une carte cadeau ou d'un forfait
 * (`purchase`). Définie dans ce module neutre pour être reprise par `pre_reservation.ts` et
 * `pack.ts` sans cycle d'import.
 *
 * `price_raw` : prix unitaire TTC en centimes (suppléments compris, avant bon). `quantity` :
 * nombre d'unités facturées. La somme des `price_raw × quantity` vaut le total TTC de l'achat.
 */
export const TrackingItemSchema = Type.Object({
  // Prestation : String(prestation_id). Forfait : `pack_<pack_id>`. Carte cadeau en montant : `gift_card`.
  item_id: Type.String(),
  item_name: Type.String(),
  price_raw: Type.Number(),
  quantity: Type.Number(),
});
export type TrackingItemObject = Static<typeof TrackingItemSchema>;
